/**
 * Theme Style Generator
 * Generates CSS code from theme styles with configurable color format
 * Adapted from shadcn-studio for OptiDev
 */

import type { ThemeStyleProps } from "@/types/theme.types";
import { colorFormatter } from "./color-converter";
import type { ColorFormat } from "./color-converter";
import { getShadowMap } from "./shadows";

interface ThemeStyles {
  light: ThemeStyleProps;
  dark: ThemeStyleProps;
}

// Default values for comparison
const DEFAULT_LETTER_SPACING = "0em";
const DEFAULT_SPACING = "0.25rem";

function generateShadowVariables(shadowMap: Record<string, string>): string {
  return `
  --shadow-2xs: ${shadowMap["shadow-2xs"]};
  --shadow-xs: ${shadowMap["shadow-xs"]};
  --shadow-sm: ${shadowMap["shadow-sm"]};
  --shadow: ${shadowMap["shadow"]};
  --shadow-md: ${shadowMap["shadow-md"]};
  --shadow-lg: ${shadowMap["shadow-lg"]};
  --shadow-xl: ${shadowMap["shadow-xl"]};
  --shadow-2xl: ${shadowMap["shadow-2xl"]};`;
}

function generateTrackingVariables(themeStyles: ThemeStyles): string {
  const styles = themeStyles["light"];

  if (styles["letter-spacing"] === "0em") {
    return "";
  }

  return `

  --tracking-tighter: calc(var(--tracking-normal) - 0.05em);
  --tracking-tight: calc(var(--tracking-normal) - 0.025em);
  --tracking-normal: var(--tracking-normal);
  --tracking-wide: calc(var(--tracking-normal) + 0.025em);
  --tracking-wider: calc(var(--tracking-normal) + 0.05em);
  --tracking-widest: calc(var(--tracking-normal) + 0.1em);`;
}

function generateThemeCode(
  styles: ThemeStyles,
  colorFormat: ColorFormat = "oklch"
): string {
  if (!("light" in styles) || !("dark" in styles)) {
    throw new Error("Invalid theme styles: missing light or dark mode");
  }

  const formatColor = (color: string | undefined) => {
    if (!color) return "";
    return colorFormatter(color, colorFormat);
  };

  const themeStyles = styles;

  // Helper to safely get value with fallback
  const getValue = (
    mode: "light" | "dark",
    key: keyof ThemeStyleProps,
    fallback = ""
  ) => {
    return themeStyles[mode][key] || fallback;
  };

  return `:root {
  --background: ${formatColor(getValue("light", "background"))};
  --foreground: ${formatColor(getValue("light", "foreground"))};
  --card: ${formatColor(getValue("light", "card"))};
  --card-foreground: ${formatColor(getValue("light", "card-foreground"))};
  --popover: ${formatColor(getValue("light", "popover"))};
  --popover-foreground: ${formatColor(getValue("light", "popover-foreground"))};
  --primary: ${formatColor(getValue("light", "primary"))};
  --primary-foreground: ${formatColor(getValue("light", "primary-foreground"))};
  --secondary: ${formatColor(getValue("light", "secondary"))};
  --secondary-foreground: ${formatColor(getValue("light", "secondary-foreground"))};
  --muted: ${formatColor(getValue("light", "muted"))};
  --muted-foreground: ${formatColor(getValue("light", "muted-foreground"))};
  --accent: ${formatColor(getValue("light", "accent"))};
  --accent-foreground: ${formatColor(getValue("light", "accent-foreground"))};
  --destructive: ${formatColor(getValue("light", "destructive"))};
  --border: ${formatColor(getValue("light", "border"))};
  --input: ${formatColor(getValue("light", "input"))};
  --ring: ${formatColor(getValue("light", "ring"))};
  --chart-1: ${formatColor(getValue("light", "chart-1"))};
  --chart-2: ${formatColor(getValue("light", "chart-2"))};
  --chart-3: ${formatColor(getValue("light", "chart-3"))};
  --chart-4: ${formatColor(getValue("light", "chart-4"))};
  --chart-5: ${formatColor(getValue("light", "chart-5"))};
  --sidebar: ${formatColor(getValue("light", "sidebar"))};
  --sidebar-foreground: ${formatColor(getValue("light", "sidebar-foreground"))};
  --sidebar-primary: ${formatColor(getValue("light", "sidebar-primary"))};
  --sidebar-primary-foreground: ${formatColor(getValue("light", "sidebar-primary-foreground"))};
  --sidebar-accent: ${formatColor(getValue("light", "sidebar-accent"))};
  --sidebar-accent-foreground: ${formatColor(getValue("light", "sidebar-accent-foreground"))};
  --sidebar-border: ${formatColor(getValue("light", "sidebar-border"))};
  --sidebar-ring: ${formatColor(getValue("light", "sidebar-ring"))};

  --font-sans: ${getValue("light", "font-sans", "ui-sans-serif, system-ui, sans-serif")};
  --font-serif: ${getValue("light", "font-serif", "ui-serif, Georgia, serif")};
  --font-mono: ${getValue("light", "font-mono", "ui-monospace, monospace")};

  --radius: ${getValue("light", "radius", "0.5rem")};
  ${generateShadowVariables(getShadowMap(themeStyles.light, colorFormat))}
  ${
    getValue("light", "letter-spacing") !== DEFAULT_LETTER_SPACING
      ? `\n  --tracking-normal: ${getValue("light", "letter-spacing")};`
      : ""
  }${getValue("light", "spacing") !== DEFAULT_SPACING ? `\n  --spacing: ${getValue("light", "spacing")};` : ""}
}

.dark {
  --background: ${formatColor(getValue("dark", "background"))};
  --foreground: ${formatColor(getValue("dark", "foreground"))};
  --card: ${formatColor(getValue("dark", "card"))};
  --card-foreground: ${formatColor(getValue("dark", "card-foreground"))};
  --popover: ${formatColor(getValue("dark", "popover"))};
  --popover-foreground: ${formatColor(getValue("dark", "popover-foreground"))};
  --primary: ${formatColor(getValue("dark", "primary"))};
  --primary-foreground: ${formatColor(getValue("dark", "primary-foreground"))};
  --secondary: ${formatColor(getValue("dark", "secondary"))};
  --secondary-foreground: ${formatColor(getValue("dark", "secondary-foreground"))};
  --muted: ${formatColor(getValue("dark", "muted"))};
  --muted-foreground: ${formatColor(getValue("dark", "muted-foreground"))};
  --accent: ${formatColor(getValue("dark", "accent"))};
  --accent-foreground: ${formatColor(getValue("dark", "accent-foreground"))};
  --destructive: ${formatColor(getValue("dark", "destructive"))};
  --border: ${formatColor(getValue("dark", "border"))};
  --input: ${formatColor(getValue("dark", "input"))};
  --ring: ${formatColor(getValue("dark", "ring"))};
  --chart-1: ${formatColor(getValue("dark", "chart-1"))};
  --chart-2: ${formatColor(getValue("dark", "chart-2"))};
  --chart-3: ${formatColor(getValue("dark", "chart-3"))};
  --chart-4: ${formatColor(getValue("dark", "chart-4"))};
  --chart-5: ${formatColor(getValue("dark", "chart-5"))};
  --sidebar: ${formatColor(getValue("dark", "sidebar"))};
  --sidebar-foreground: ${formatColor(getValue("dark", "sidebar-foreground"))};
  --sidebar-primary: ${formatColor(getValue("dark", "sidebar-primary"))};
  --sidebar-primary-foreground: ${formatColor(getValue("dark", "sidebar-primary-foreground"))};
  --sidebar-accent: ${formatColor(getValue("dark", "sidebar-accent"))};
  --sidebar-accent-foreground: ${formatColor(getValue("dark", "sidebar-accent-foreground"))};
  --sidebar-border: ${formatColor(getValue("dark", "sidebar-border"))};
  --sidebar-ring: ${formatColor(getValue("dark", "sidebar-ring"))};
  ${generateShadowVariables(getShadowMap(themeStyles.dark, colorFormat))}
}

@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-ring: var(--sidebar-ring);

  --font-sans: var(--font-sans);
  --font-mono: var(--font-mono);
  --font-serif: var(--font-serif);

  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);

  --shadow-2xs: var(--shadow-2xs);
  --shadow-xs: var(--shadow-xs);
  --shadow-sm: var(--shadow-sm);
  --shadow: var(--shadow);
  --shadow-md: var(--shadow-md);
  --shadow-lg: var(--shadow-lg);
  --shadow-xl: var(--shadow-xl);
  --shadow-2xl: var(--shadow-2xl);${generateTrackingVariables(themeStyles)}
}${themeStyles["light"]["letter-spacing"] !== "0em" ? "\n\nbody {\n  letter-spacing: var(--tracking-normal);\n}" : ""}`;
}

export { generateThemeCode };
export type { ThemeStyles };
